import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import "./CreditFlow.scss";
import { CreditFlowService } from "../../services/CreditFlowService";
import { CreditFlowTokenResponseDto } from "../../models/CreditFlow";
import Swal from "sweetalert2";
import Page from "../containers/page";

interface IState {
    iframeUrl: string;
    error: string | null;
    loading: boolean;
    noPreoffer: boolean;
}

interface IProps { }

const CreditFlow: React.FC<IProps> = () => {
    const { t } = useTranslation();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [state, setState] = useState<IState>({
        iframeUrl: "",
        error: null,
        loading: true,
        noPreoffer: false
    });

    useEffect(() => {
        let cleanupListener: (() => void) | undefined;
        let isMounted = true;

        // Pide el token al backend (el backend obtiene el merchant del contexto JWT)
        CreditFlowService.getCreditFlowToken()
            .then((response: CreditFlowTokenResponseDto) => {
                if (!isMounted) {
                    return;
                }

                // Si da false en ShowCreditModule que viene de la descerialización de data indica que no se debe mostrar el módulo, no renderizamos el iframe
                if (!response.showCreditModule) {
                    setState(prevState => ({
                        ...prevState,
                        loading: false,
                        noPreoffer: true
                    }));
                    return;
                }

                setState(prevState => ({
                    ...prevState,
                    iframeUrl: response.iframeUrl ?? "",
                    loading: false
                }));

                // Configura el listener de postMessage (guarda el cleanup para ejecutarlo al desmontar)
                cleanupListener = setupPostMessageListener(response.token ?? "", response.iframeUrl ?? "");
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }
                console.error("Error getting CreditFlow token:", error);
                setState(prevState => ({
                    ...prevState,
                    error: error.message || "Failed to get CreditFlow token",
                    loading: false
                }));
                Swal.fire({
                    title: t('errorOcurred'),
                    html: error.message || t("contactSupport"),
                    confirmButtonColor: '#f60',
                    timer: 6000,
                    timerProgressBar: true,
                    icon: "warning"
                });
            });

        // Cleanup al desmontar: remueve el listener y limpia cualquier timeout pendiente
        return () => {
            isMounted = false;
            if (cleanupListener) {
                cleanupListener();
            }
        };
    }, []);

    const setupPostMessageListener = (token: string, iframeUrl: string) => {
        const handleMessage = (event: MessageEvent) => {
            // Siempre validar el origen
            const iframeOrigin = new URL(iframeUrl).origin;
            if (event.origin !== iframeOrigin) {
                console.warn("Received message from untrusted origin:", event.origin);
                return;
            }

            switch (event.data.type) {
                case 'READY':
                    // el iframe está listo, enviamos la autenticación
                    console.log("CreditFlow iframe is ready");
                    sendAuthToIframe(token, iframeUrl);
                    break;

                case 'AUTH_SUCCESS':
                    // Autenticación exitosa - limpiamos el timeout
                    if (authTimeoutRef.current) {
                        clearTimeout(authTimeoutRef.current);
                        authTimeoutRef.current = null;
                    }
                    console.log("CreditFlow authentication successful", event.data);
                    // el iframe redirige automáticamente a /credit-flow/merchant-{id}
                    break;

                case 'AUTH_ERROR':
                    // Autenticación fallida - limpiamos el timeout
                    if (authTimeoutRef.current) {
                        clearTimeout(authTimeoutRef.current);
                        authTimeoutRef.current = null;
                    }
                    console.error("CreditFlow authentication error:", event.data.error);
                    Swal.fire({
                        title: t('errorOcurred'),
                        html: event.data.error || t("contactSupport"),
                        confirmButtonColor: '#f60',
                        timer: 6000,
                        timerProgressBar: true,
                        icon: "warning"
                    });
                    break;
                default:
                    console.warn("Unknown message type:", event.data.type);
            }
        };

        window.addEventListener('message', handleMessage);

        // Remueve el listener al desmontar
        return () => {
            window.removeEventListener('message', handleMessage);
            if (authTimeoutRef.current) {
                clearTimeout(authTimeoutRef.current);
                authTimeoutRef.current = null;
            }
        };
    };

    const sendAuthToIframe = (token: string, iframeUrl: string) => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) {
            console.error("iframe not available");
            return;
        }

        // Obtiene la URL base sin path ni query params
        const iframeOrigin = new URL(iframeUrl).origin;
        iframeRef.current.contentWindow.postMessage(
            {
                type: 'AUTH',
                token: token
            },
            iframeOrigin  // Origen exacto, nunca '*'
        );

        // Timeout para la respuesta del iframe
        // Si el origen no está autorizado, el iframe no responde - el timeout lo detecta
        authTimeoutRef.current = setTimeout(() => {
            console.error("CreditFlow iframe did not respond within timeout");
            Swal.fire({
                title: t('errorOcurred'),
                html: t("contactSupport"),
                confirmButtonColor: '#f60',
                timer: 6000,
                timerProgressBar: true,
                icon: "warning"
            });
            authTimeoutRef.current = null;
        }, 10000); // 10 segundos - si el origen no está autorizado, el iframe bloquea el acceso sin responder
    };

    return (
        <React.Fragment>
            <Page>
                {state.loading ? (
                    <div className="creditflow-loading">
                        <p>{t('loading')}</p>
                    </div>
                ) : state.error ? (
                    <div className="creditflow-error">
                        <p>{state.error}</p>
                    </div>
                ) : state.noPreoffer ? (
                    <div className="creditflow-empty">
                        <p>{t('creditflowNoPreoffer')}</p>
                    </div>
                ) : (
                    <iframe
                        ref={iframeRef}
                        className='creditflow-frame'
                        src={state.iframeUrl}
                        title="CreditFlow Module"
                        allow="payment"
                    ></iframe>
                )}
            </Page>
        </React.Fragment>
    );
};

export default CreditFlow;
